import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle, XCircle, Warning, Clock, Receipt } from "@phosphor-icons/react";

/**
 * Post-Payroll Correction Queue (Iter 3d).
 *
 * Shown as a tab on the Attendance page. Lists every attendance
 * correction request created when an admin edits a retro row that
 * falls inside a posted payroll_run. Payroll managers (and owner /
 * admin) can approve / reject; requesters can cancel their own
 * pending items.
 */
const STATUS_LABEL = {
  submitted: { label: "Pending review", color: "bg-amber-100 text-amber-800 border-amber-200" },
  approved:  { label: "Approved",       color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected:  { label: "Rejected",       color: "bg-rose-100 text-rose-800 border-rose-200" },
  cancelled: { label: "Cancelled",      color: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function AttendanceCorrectionsPanel() {
  const [items, setItems]   = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [status, setStatus] = useState("submitted");
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // { row, action } modal
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== "all") params.status = status;
      const { data } = await api.get("/attendance/corrections", { params });
      setItems(data.corrections || []);
      setCanReview(!!data.can_review);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openDecision = (row, action) => {
    setDecision({ row, action });
    setReason("");
  };

  const submitDecision = async () => {
    if (!decision) return;
    const { row, action } = decision;
    if (action === "reject" && !reason.trim()) {
      toast.error("A reason is required to reject");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/attendance/corrections/${row.id}/${action}`, { reason });
      toast.success(action === "approve" ? "Correction approved — payroll flagged for recalc" : "Correction rejected");
      setDecision(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelOwn = async (row) => {
    if (!window.confirm("Cancel this correction request?")) return;
    try {
      await api.post(`/attendance/corrections/${row.id}/cancel`);
      toast.success("Correction cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="corrections-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          Retro edits to attendance rows that fall inside a posted payroll run wait for
          payroll-manager review here. Approval applies the change and marks the
          run <em>needs&nbsp;recalc</em>.
        </div>
        <div className="flex-none">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="corrections-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="submitted">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Loading corrections…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm" data-testid="corrections-empty">
              <Receipt size={28} className="mx-auto opacity-50 mb-2" />
              No {status === "all" ? "" : STATUS_LABEL[status]?.label?.toLowerCase()} corrections.
            </div>
          ) : (
            <div className="divide-y">
              {items.map(row => (
                <CorrectionRow
                  key={row.id}
                  row={row}
                  canReview={canReview}
                  onApprove={() => openDecision(row, "approve")}
                  onReject={() => openDecision(row, "reject")}
                  onCancel={() => cancelOwn(row)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent className="sm:max-w-md" data-testid="correction-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision?.action === "approve" ? "Approve correction" : "Reject correction"}
            </DialogTitle>
            <DialogDescription>
              {decision?.action === "approve"
                ? "Applying this changes the attendance row and marks the payroll run for recalculation. Add a note for your audit trail (optional)."
                : "Provide a reason — this becomes part of the audit trail visible to the requester."}
            </DialogDescription>
          </DialogHeader>
          {decision?.row && (
            <div className="rounded border p-3 bg-muted/30 text-xs space-y-1">
              <div><b>{decision.row.subject_name || decision.row.subject_id}</b> · {decision.row.date}</div>
              <div>
                <StatusPill status={decision.row.from_status} /> → <StatusPill status={decision.row.to_status} />
              </div>
              {decision.row.to_note && <div className="text-muted-foreground italic">&ldquo;{decision.row.to_note}&rdquo;</div>}
            </div>
          )}
          <Textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={decision?.action === "reject" ? "Reason for rejection (required)" : "Note (optional)"}
            data-testid="correction-decision-reason"
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitDecision}
              disabled={busy}
              variant={decision?.action === "reject" ? "destructive" : "default"}
              data-testid="correction-decision-submit"
            >
              {busy ? "…" : (decision?.action === "approve" ? "Approve" : "Reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CorrectionRow({ row, canReview, onApprove, onReject, onCancel }) {
  const statusMeta = STATUS_LABEL[row.status] || { label: row.status, color: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <div className="p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`correction-row-${row.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{row.subject_name || row.subject_id}</span>
          <Badge variant="outline" className="text-[10px]">{row.date}</Badge>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusMeta.color}`}>
            {statusMeta.label}
          </span>
          {row.is_post_window && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">
              <Warning size={10} /> Past correction window
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
          <StatusPill status={row.from_status} />
          <span>→</span>
          <StatusPill status={row.to_status} />
          {row.to_note && <span className="italic">· &ldquo;{row.to_note}&rdquo;</span>}
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          Requested by {row.requested_by_email} · {row.requested_at?.slice(0,16).replace("T"," ")}
          {row.reviewer_email && (
            <> · reviewed by {row.reviewer_email}{row.reviewer_note ? ` — ${row.reviewer_note}` : ""}</>
          )}
        </div>
      </div>
      <div className="flex-none flex gap-2">
        {row.status === "submitted" && canReview && (
          <>
            <Button size="sm" variant="outline" onClick={onReject} data-testid={`correction-reject-${row.id}`}>
              <XCircle size={14} className="mr-1" /> Reject
            </Button>
            <Button size="sm" onClick={onApprove} data-testid={`correction-approve-${row.id}`}>
              <CheckCircle size={14} className="mr-1" /> Approve
            </Button>
          </>
        )}
        {row.status === "submitted" && !canReview && (
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`correction-cancel-${row.id}`}>
            <Clock size={14} className="mr-1" /> Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 uppercase tracking-wide">
      {status}
    </span>
  );
}
